import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, FormHelperText, InputAdornment, Link, MenuItem, Radio,
  RadioGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { toast } from "react-toastify";
import api from "../../api";

const WALLET_LABELS = { google_pay: "Google Pay", apple_pay: "Apple Pay", link: "Link", samsung_pay: "Samsung Pay" };

const REASONS = [
  { value: "requested_by_customer", label: "Requested by customer" },
  { value: "duplicate", label: "Duplicate payment" },
  { value: "fraudulent", label: "Fraudulent" },
];

/** Matches the API's minimum for the (optional) written refund note. */
const MIN_NOTE = 5;

export const REFUND_ACCESS_WARNING =
  "Refunding any amount removes the customer's access to this purchase. For basket payments refunded in Stripe, all items in the basket lose access.";

/** Mirrors RefundRequest::REJECTION_REASONS on the backend. Shown to the customer. */
export const REJECTION_REASONS = [
  { key: "outside_window", label: "Outside the refund window" },
  { key: "already_used", label: "Resource already used/accessed" },
  { key: "duplicate_request", label: "Duplicate request" },
  { key: "not_eligible", label: "Not eligible under refund policy" },
  { key: "insufficient_information", label: "Insufficient information provided" },
  { key: "subscription_already_cancelled", label: "Subscription already cancelled" },
];

const REFUND_STATUS_COLORS = { succeeded: "success", pending: "info", requires_action: "warning", failed: "error", canceled: "default" };

export const money = (amount, currency = "gbp") => {
  const code = String(currency || "gbp").toUpperCase();
  const value = Number(amount || 0).toFixed(2);
  return code === "GBP" ? `£${value}` : `${code} ${value}`;
};

/** Paid / Partially refunded / Refunded / Refund pending chip for a list row. */
export function RefundStateChip({ row }) {
  const state = row?.refund_state || "none";
  let chip;
  if (state === "full" || (state === "none" && row?.access_revoked)) {
    chip = <Chip size="small" label="Refunded" color="default" />;
  } else if (state === "partial") {
    chip = <Chip size="small" label={`Part refunded ${money(row.refunded_amount, row.currency)}`} color="warning" />;
  } else if (state === "pending") {
    chip = <Chip size="small" label="Refund pending" color="info" />;
  } else {
    chip = <Chip size="small" label="Paid" color="success" variant="outlined" />;
  }

  return (
    <Box>
      {chip}
      {row?.access_revoked && (
        <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.25 }}>
          Access revoked
        </Typography>
      )}
    </Box>
  );
}

function paidWith(payment) {
  if (!payment) return "—";
  const wallet = WALLET_LABELS[payment.wallet] || null;
  const card = payment.brand
    ? `${payment.brand.charAt(0).toUpperCase()}${payment.brand.slice(1)}${payment.last4 ? ` •••• ${payment.last4}` : ""}`
    : null;
  if (wallet && card) return `${wallet} · ${card}`;
  if (wallet) return wallet;
  if (card) return `Card · ${card}`;
  return payment.method ? payment.method.replace(/_/g, " ") : "—";
}

/**
 * Refund a Stripe payment (card, Google Pay or Apple Pay) from the billing pages.
 *
 * `row` needs purchasable_type, purchase_id and, for the header, student_name /
 * student_email / product. Calls onDone() after a refund or revoke so the list refetches.
 *
 * With `refundRequest` (a customer's request from the Refund Requests tab) the dialog
 * becomes the decision: "Approve & refund" issues the refund through the request, and
 * "Reject" closes it with a note the customer is emailed.
 */
export default function RefundTransactionDialog({ open, row, refundRequest, onClose, onDone }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [noteTouched, setNoteTouched] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectReasons, setRejectReasons] = useState([]);
  const [overrideUsage, setOverrideUsage] = useState(false);
  const deciding = !!refundRequest && ["pending", "processing"].includes(refundRequest.status);

  const endpoint = row ? `admin/billing/transactions/${row.purchasable_type}/${row.purchase_id}` : null;

  useEffect(() => {
    if (!open || !endpoint) return;
    setDetails(null);
    setMode("full");
    setPartialAmount("");
    setReason("requested_by_customer");
    setNote("");
    setNoteTouched(false);
    setRejecting(false);
    setRejectNote("");
    setRejectReasons([]);
    setOverrideUsage(false);
    setLoading(true);
    api
      .get(`${endpoint}/refunds`)
      .then((res) => setDetails(res.data?.data || null))
      .catch((e) => {
        toast.error(e.response?.data?.message || "Failed to load payment details");
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint]);

  const currency = details?.currency || row?.currency || "gbp";
  const refundable = Number(details?.refundable_amount || 0);
  const alreadyRefunded = (details?.refunds || [])
    .filter((r) => ["succeeded", "pending", "requires_action"].includes(r.status))
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const amount = useMemo(() => {
    if (mode === "full") return refundable;
    const parsed = Number.parseFloat(partialAmount);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
  }, [mode, partialAmount, refundable]);

  const amountError = mode === "partial" && partialAmount !== "" && (amount <= 0 || amount > refundable)
    ? `Enter between ${money(0.01, currency)} and ${money(refundable, currency)}`
    : "";

  const handleModeChange = (value) => {
    setMode(value);
  };

  const submitRefund = async () => {
    try {
      setSubmitting(true);
      const url = deciding ? `admin/billing/refund-requests/${refundRequest.id}/approve` : `${endpoint}/refund`;
      // Any succeeded refund removes access, so revoke_access is no longer sent.
      const payload = { amount, reason };
      if (planUsed && overrideUsage) payload.override_usage = true;
      if (note.trim()) payload.note = note.trim();
      const res = await api.post(url, payload);
      toast.success(res.data?.message || "Refund issued");
      setDetails(res.data?.data || null);
      onDone?.();
      onClose();
    } catch (e) {
      const errors = e.response?.data?.errors;
      const first = errors ? Object.values(errors).flat()[0] : null;
      toast.error(first || e.response?.data?.message || "Refund failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    try {
      setSubmitting(true);
      const payload = { reasons: rejectReasons };
      if (rejectNote.trim()) payload.note = rejectNote.trim();
      const res = await api.post(`admin/billing/refund-requests/${refundRequest.id}/reject`, payload);
      toast.success(res.data?.message || "Request rejected");
      onDone?.();
      onClose();
    } catch (e) {
      const errors = e.response?.data?.errors;
      const first = errors ? Object.values(errors).flat()[0] : null;
      toast.error(first || e.response?.data?.message || "Could not reject the request");
    } finally {
      setSubmitting(false);
    }
  };

  const submitRevoke = async () => {
    try {
      setSubmitting(true);
      const res = await api.post(`${endpoint}/revoke-access`);
      toast.success(res.data?.message || "Access revoked");
      onDone?.();
      onClose();
    } catch (e) {
      const errors = e.response?.data?.errors;
      const first = errors ? Object.values(errors).flat()[0] : null;
      toast.error(first || e.response?.data?.message || "Could not revoke access");
    } finally {
      setSubmitting(false);
    }
  };

  const canRefund = !!details && refundable > 0 && !details.payment?.stripe_error;
  const canRevokeOnly = !!details && !canRefund && !details.access_revoked && alreadyRefunded > 0;
  const planUsage = details?.refund_policy?.subscription_usage || null;
  const planUsed = !!planUsage?.used;
  const confirmDisabled = (planUsed && !overrideUsage) || submitting || !canRefund || amount <= 0 || amount > refundable || !!amountError
    || (mode === "partial" && partialAmount === "")
    || !reason || (note.trim() !== "" && note.trim().length < MIN_NOTE);
  const rejectValid = rejectReasons.length > 0 || rejectNote.trim() !== "";
  const toggleRejectReason = (key) => setRejectReasons((prev) => (
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  ));

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex", alignItems: "center", gap: 1, color: "#fff", fontWeight: 700,
          background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
        }}
      >
        <CurrencyExchangeIcon /> {deciding ? "Review refund request" : "Refund payment"}
      </DialogTitle>

      <DialogContent dividers>
        {loading || !details ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">{row?.student_name || "—"}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{row?.student_email}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{row?.product || "—"}</Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <Detail label="Paid with" value={paidWith(details.payment)} />
              <Detail label="Amount paid" value={money(details.amount, currency)} />
              <Detail label="Already refunded" value={money(alreadyRefunded, currency)} />
              <Detail label="Refundable now" value={money(refundable, currency)} strong />
              <Detail
                label="Payment"
                value={
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {details.payment?.payment_intent_id || "—"}
                  </Typography>
                }
              />
              {details.payment?.receipt_url && (
                <Detail
                  label="Receipt"
                  value={<Link href={details.payment.receipt_url} target="_blank" rel="noopener">Stripe receipt</Link>}
                />
              )}
            </Box>

            {refundRequest && (
              <Alert severity={deciding ? "info" : "success"} icon={false}>
                <Typography variant="subtitle2">
                  Customer's request
                  {refundRequest.created_at ? ` · ${new Date(refundRequest.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{refundRequest.reason}</Typography>
                {!deciding && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {refundRequest.status === "approved" ? "Approved" : refundRequest.status === "rejected" ? "Rejected" : "Withdrawn by the customer"}
                    {refundRequest.decided_by ? ` by ${refundRequest.decided_by}` : ""}
                    {(refundRequest.rejection_summary || refundRequest.admin_note) ? ` — ${refundRequest.rejection_summary || refundRequest.admin_note}` : ""}
                  </Typography>
                )}
              </Alert>
            )}

            {deciding && rejecting && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Reason for rejecting (shown to the customer and emailed to them)
                </Typography>
                <Stack>
                  {REJECTION_REASONS.map((r) => (
                    <FormControlLabel
                      key={r.key}
                      control={<Checkbox size="small" checked={rejectReasons.includes(r.key)} onChange={() => toggleRejectReason(r.key)} />}
                      label={r.label}
                    />
                  ))}
                </Stack>
                <TextField
                  fullWidth size="small" label="Custom reason (optional with a selected reason)" value={rejectNote}
                  multiline minRows={2} onChange={(e) => setRejectNote(e.target.value)}
                  inputProps={{ maxLength: 1000 }} sx={{ mt: 1 }}
                />
                <FormHelperText error={!rejectValid}>
                  {rejectValid ? "The customer sees the selected reasons and your custom text." : "Select at least one reason or write a custom reason."}
                </FormHelperText>
              </Box>
            )}

            <PolicyLine policy={details.refund_policy} claim={refundRequest?.claim} />
            {planUsage && (
              <Alert severity={planUsed ? "error" : "success"}>
                <strong>{planUsage.label}</strong>
                {planUsed
                  ? ". A used plan is not eligible for a refund."
                  : ". Nothing on this plan has been used since the payment, so it can be refunded."}
              </Alert>
            )}
            {canRefund && details.refund_policy?.suggested_amount > 0
              && details.refund_policy.suggested_amount < refundable && (
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    onClick={() => { setMode("partial"); setPartialAmount(String(details.refund_policy.suggested_amount)); }}
                  >
                    Use
                  </Button>
                }
              >
                Pro-rata for the unused part of the period: {money(details.refund_policy.suggested_amount, currency)}
              </Alert>
            )}

            {details.payment?.stripe_error && (
              <Alert severity="error">Stripe: {details.payment.stripe_error}</Alert>
            )}
            {!details.payment?.payment_intent_id && (
              <Alert severity="info">
                This payment was not taken through Stripe (offline or bank transfer), so it cannot be refunded here.
              </Alert>
            )}

            {canRefund && !rejecting && (!refundRequest || deciding) && (
              <>
                <Divider />
                <RadioGroup row value={mode} onChange={(e) => handleModeChange(e.target.value)}>
                  <FormControlLabel value="full" control={<Radio />} label={`Full (${money(refundable, currency)})`} />
                  <FormControlLabel value="partial" control={<Radio />} label="Partial" />
                </RadioGroup>
                {mode === "partial" && (
                  <TextField
                    size="small" type="number" label="Amount to refund" value={partialAmount}
                    onChange={(e) => setPartialAmount(e.target.value)}
                    inputProps={{ min: 0.01, max: refundable, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currency.toLowerCase() === "gbp" ? "£" : currency.toUpperCase()}</InputAdornment>,
                    }}
                    error={!!amountError}
                    helperText={amountError || `Up to ${money(refundable, currency)}`}
                  />
                )}
                <TextField
                  select required size="small" label="Refund reason" value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  helperText="Sent to Stripe with the refund."
                >
                  {REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" label="Note (optional)" value={note} multiline minRows={2}
                  onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 1000 }}
                  error={noteTouched && note.trim() !== "" && note.trim().length < MIN_NOTE}
                  onBlur={() => setNoteTouched(true)}
                  placeholder="e.g. Customer bought the wrong paper; confirmed by email"
                  helperText={
                    noteTouched && note.trim() !== "" && note.trim().length < MIN_NOTE
                      ? `If you add a note, use at least ${MIN_NOTE} characters.`
                      : "Optional. Kept in the refund history and audit log."
                  }
                />
                <Alert severity="error">{REFUND_ACCESS_WARNING}</Alert>
                {planUsed && (
                  <FormControlLabel
                    control={<Checkbox checked={overrideUsage} onChange={(e) => setOverrideUsage(e.target.checked)} />}
                    label="Override: refund this plan even though it has been used (recorded in the audit log)"
                  />
                )}
                <Alert severity="warning">
                  {money(amount, currency)} goes back to the original payment method ({paidWith(details.payment)}),
                  usually within 5–10 business days. A refund cannot be undone.
                </Alert>
              </>
            )}

            {canRevokeOnly && (
              <Alert severity="info">
                Fully refunded, but the student still has access. Revoke it if they should lose the purchase.
              </Alert>
            )}
            {details.access_revoked && refundable <= 0 && (
              <Alert severity="success">Refunded and access revoked.</Alert>
            )}

            {(details.refunds || []).length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Refund history</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.refunds.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{money(r.amount, r.currency)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={r.status.replace(/_/g, " ")} color={REFUND_STATUS_COLORS[r.status] || "default"} />
                          {r.failure_reason && (
                            <Typography variant="caption" display="block" color="error.main">{r.failure_reason}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {r.source === "stripe_webhook" ? "Stripe dashboard" : r.initiated_by || "—"}
                          </Typography>
                          {r.admin_note && (
                            <Typography variant="caption" color="text.secondary">{r.admin_note}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={rejecting ? () => setRejecting(false) : onClose} disabled={submitting}>
          {rejecting ? "Back" : "Cancel"}
        </Button>
        {deciding && !rejecting && (
          <Button color="inherit" variant="outlined" onClick={() => setRejecting(true)} disabled={submitting}>
            Reject
          </Button>
        )}
        {deciding && rejecting && (
          <Button color="warning" variant="contained" onClick={submitReject} disabled={submitting || !rejectValid}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : "Reject request"}
          </Button>
        )}
        {canRevokeOnly && !refundRequest && (
          <Button color="error" variant="outlined" onClick={submitRevoke} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : "Revoke access"}
          </Button>
        )}
        {canRefund && !rejecting && (!refundRequest || deciding) && (
          <Button color="error" variant="contained" onClick={submitRefund} disabled={confirmDisabled}>
            {submitting
              ? <CircularProgress size={20} color="inherit" />
              : `${deciding ? "Approve & refund" : "Refund"} ${money(amount, currency)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The customer refund policy (14-day window, used or not, consent on record) as a hint
 * for the admin. Admins can refund regardless; this says what the customer could ask for.
 */
function PolicyLine({ policy, claim }) {
  if (!policy || ["not_stripe", "refunded"].includes(policy.reason_code)) return null;

  const windowText = policy.reason_code === "window_closed"
    ? `Outside the ${policy.window_days}-day window`
    : `Day ${Math.max(1, policy.window_days - (policy.days_left || 0))} of ${policy.window_days}`;
  const usage = policy.used_how
    ? `${policy.used_how.replace(/^(\w)/, (c) => c.toUpperCase())} ${policy.used_at ? new Date(policy.used_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ""}`.trim()
    : "Unused";
  const parts = [windowText, usage];
  if (policy.used_how) parts.push(policy.consent_recorded ? "immediate-supply consent recorded" : "no consent on record");
  if (claim === "faulty") parts.push("faulty-item claim");

  return (
    <Alert severity={policy.eligible || claim === "faulty" ? "success" : "warning"} icon={false} sx={{ py: 0.25 }}>
      <Typography variant="body2">
        <strong>Refund policy:</strong> {parts.join(" · ")}
        {!policy.eligible && claim !== "faulty" ? " — outside what the customer can claim; refund only if you choose to." : ""}
      </Typography>
    </Alert>
  );
}

function Detail({ label, value, strong }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      {typeof value === "string" ? (
        <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>{value}</Typography>
      ) : value}
    </Box>
  );
}
